import {Button} from '@wandb/components';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {isVersionAlias} from '@wandb/weave/common/util/artifacts';
import {TAG_COLOR_CONTRAST_WHEEL} from '@wandb/weave/components/Tag/utils';
import * as React from 'react';
import {useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Header, Modal} from 'semantic-ui-react';

import * as Generated from '../generated/graphql';
import {TagAddButton} from './TagAddButton';
// @todo: Migrate to 'TagGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tag, TagType} from './Tags';

interface AliasProps {
  isProtected?: boolean;
  alias: Pick<Generated.ArtifactAlias, 'alias'>;
  onDelete?: () => Promise<any>;
  onClick?(): void;
  skipModal?: boolean;
}

export const Alias = (props: AliasProps) => {
  const {alias, onDelete, onClick, isProtected, skipModal} = props;
  const [isModalOpen, setModalOpen] = useState(false);
  const [isRemoving, setRemoving] = useState(false);

  const onTagDeleteAction = useMemo(() => {
    if (onDelete) {
      if (skipModal) {
        return onDelete;
      } else {
        return () => setModalOpen(true);
      }
    }
    return undefined;
  }, [onDelete, skipModal]);

  return (
    <>
      <Tag
        tag={{
          name: alias.alias,
          colorIndex: isProtected ? TagType.PROTECTED_ALIAS : TagType.ALIAS,
        }}
        noun={isProtected ? 'protected-alias' : 'alias'}
        onDelete={onTagDeleteAction}
        onClick={onClick}
      />
      <Modal open={isModalOpen} onClose={() => setModalOpen(false)}>
        <Header>
          <p>
            Are you sure you want to remove the <b>{alias.alias}</b> alias?
          </p>
        </Header>
        <Modal.Content>
          This will break any scripts that use this alias!
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onPress={() => setModalOpen(false)}>
            Nevermind
          </Button>
          <Button
            ml={4}
            isPending={isRemoving}
            variant="destructive"
            onPress={async () => {
              setRemoving(true);

              if (onDelete) {
                await onDelete();
              }

              setRemoving(false);
              setModalOpen(false);
            }}>
            Remove alias
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

interface AddAliasButtonProps {
  aliases: Array<Pick<Generated.ArtifactAlias, 'alias'>>;
  availableAliases: Array<
    Pick<Generated.ArtifactAlias, 'alias'> & {isProtected?: boolean}
  >;
  onCreate: (newAlias: string) => Promise<any>;
}

export const AddAliasButton = (props: AddAliasButtonProps) => {
  const {aliases, availableAliases, onCreate} = props;

  return (
    <TagAddButton
      tags={aliases.map(a => {
        return {name: a.alias, colorIndex: TagType.ALIAS};
      })}
      availableTags={availableAliases.map(a => {
        // use TAG_COLOR_CONTRAST_WHEEL to map alias type to color index
        // directly instead of using the deprecated TagType enum
        const colorIndex = a.isProtected
          ? TAG_COLOR_CONTRAST_WHEEL.indexOf('moon')
          : TAG_COLOR_CONTRAST_WHEEL.indexOf('sienna');
        return {
          name: a.alias,
          colorIndex: colorIndex === -1 ? undefined : colorIndex,
        };
      })}
      addTag={async tag => {
        if (isVersionAlias({alias: tag.name})) {
          toast('Custom aliases matching /^v(d+)$/ are not allowed.');
          return;
        }
        await onCreate(tag.name);
      }}
      compact={true}
      direction="bottom right"
      noun="alias"
    />
  );
};
